import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check, Lock } from 'lucide-react'
import { apptStatusInfo, apptStatusOptionsFor, isSystemApptStatus } from '../data/appointmentStatuses.js'

/**
 * Appointment-status picker — the one control that sets `appointment.status`
 * (ApptEditDrawer, so both the patient page and the clinic calendar).
 *
 * It replaces a native <select> for two reasons:
 *
 * 1. LOCKED STATUSES. A native menu cannot both show a status and refuse to
 *    assign it. C★ and P★ mean the PATIENT confirmed or moved the booking via
 *    SMS / the Patient Portal, so staff must never set one — but a row holding
 *    one has to say so. With <option>s built only from the writable list, React
 *    fell back to selecting the first option and a C* booking read as
 *    "— ไม่ระบุ —". See apptStatusOptionsFor() for the full post-mortem; the
 *    `locked` option it returns is what this component renders greyed out.
 * 2. COLOUR. Staff read the status off a coloured badge everywhere else in the
 *    app, and `<option style>` is unreliable (Safari on macOS ignores it). A
 *    custom menu gets the same tone dot + pill as every other status badge.
 *
 * `original` is the status the row is SAVED with — '' when creating. It, not
 * `value`, decides which options exist, so a mis-click on a ★ row can be undone
 * without saving and reopening.
 *
 * `className` is the caller's field styling, so the trigger lines up with the
 * plain <select>s beside it in the form grid.
 */
export default function ApptStatusSelect({ value, original = '', onChange, disabled, className = '' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const options = apptStatusOptionsFor(original)
  const code = String(value ?? '').trim()
  const info = apptStatusInfo(code)
  const locked = isSystemApptStatus(original)

  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
        title={[info.label, info.hint].filter(Boolean).join(' — ')}
        className={`${className} flex items-center gap-2 text-left ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
        <span className={`w-2 h-2 rounded-full flex-none ring-1 ring-black/5 ${info.dot}`} />
        <span className={`truncate ${code ? '' : 'text-slate-400'}`}>{code ? info.label : '— ไม่ระบุ —'}</span>
        {code && (
          <span className={`ml-auto flex-none inline-flex items-center justify-center min-w-[30px] px-1.5 py-0.5 rounded-md text-[12px] font-bold ring-1 ${info.pill}`}>{code}</span>
        )}
        <ChevronDown size={15} className={`flex-none text-slate-400 ${code ? '' : 'ml-auto'}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full min-w-[280px] max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10 p-1">
          {options.map((o) => {
            const t = apptStatusInfo(o.code)
            const sel = o.code === code
            // A locked option is pickable only as itself — i.e. as an undo. It is
            // only ever generated for the status the row already holds, so this
            // can never assign a ★ to a booking that did not earn one.
            const off = o.locked && !sel
            return (
              <button key={o.code || '_none'} type="button" disabled={off}
                onClick={() => { if (!off) { onChange?.(o.code); setOpen(false) } }}
                title={o.locked ? 'ระบบตั้งให้เมื่อคนไข้ตอบเอง — พนักงานตั้งไม่ได้' : t.hint}
                className={`flex w-full items-center gap-2.5 text-left px-2.5 py-2 rounded-lg text-[14px] transition-colors ${
                  off ? 'opacity-55 cursor-not-allowed' : 'hover:bg-slate-50'} ${sel ? 'bg-slate-50 font-medium' : ''}`}>
                <span className={`w-2 h-2 rounded-full flex-none ring-1 ring-black/5 ${o.code ? t.dot : 'bg-slate-300'}`} />
                <span className="flex-1 truncate text-slate-700">{o.label}</span>
                {o.locked && <Lock size={12} className="flex-none text-slate-400" />}
                {sel && <Check size={14} strokeWidth={3} className="flex-none text-brand-600" />}
              </button>
            )
          })}
        </div>
      )}

      {/* Only on a ★ row: say who set it, so nobody reads the greyed row as a bug. */}
      {locked && (
        <div className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-snug text-slate-400">
          <Lock size={11} className="mt-0.5 flex-none" />
          <span>
            ★ = คนไข้ยืนยัน/แจ้งเลื่อนเองผ่าน SMS หรือ Patient Portal — พนักงานตั้งเองไม่ได้
            {code !== original && ' · เลือกแถวที่ล็อกไว้เพื่อคืนค่าเดิม'}
          </span>
        </div>
      )}
    </div>
  )
}
